import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from './api';
import { API_URL } from './api';
import { useAuth } from '../context/AuthContext';

const RepresentativeContext = createContext();

export function RepresentativeProvider({ children }) {
  const { user } = useAuth();
  const [isRepresentative, setIsRepresentative] = useState(false);
  const [repProfileId, setRepProfileId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [explanations, setExplanations] = useState({});

  const loadExplanations = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/representative/explanations`);
      const expMap = {};
      (response.data.explanations || []).forEach(exp => {
        if (exp.poll_id) {
          expMap[exp.poll_id] = exp.explanation;
        } else if (exp.custom_poll_id) {
          expMap[`custom_${exp.custom_poll_id}`] = exp.explanation;
        }
      });
      setExplanations(expMap);
    } catch (error) {
      console.error('Failed to load explanations:', error);
    }
  }, []);

  const checkRepresentativeStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/user/is-representative`);
      setIsRepresentative(response.data.is_representative);
      setRepProfileId(response.data.profile_id);
      
      // Load explanations if representative
      if (response.data.is_representative) {
        loadExplanations();
      }
    } catch (error) {
      console.error('Failed to check representative status:', error);
      setIsRepresentative(false);
    } finally {
      setLoading(false);
    }
  }, [loadExplanations]);

  useEffect(() => {
    if (user) {
      checkRepresentativeStatus();
    } else {
      setIsRepresentative(false);
      setRepProfileId(null);
      setLoading(false);
    }
  }, [user, checkRepresentativeStatus]);

  const updateExplanation = (pollId, explanation, isCustomPoll = false) => {
    const key = isCustomPoll ? `custom_${pollId}` : pollId;
    setExplanations(prev => ({
      ...prev,
      [key]: explanation
    }));
  };

  const getExplanation = (pollId, isCustomPoll = false) => {
    const key = isCustomPoll ? `custom_${pollId}` : pollId;
    return explanations[key] || null;
  };

  const refreshStatus = () => {
    if (user) {
      checkRepresentativeStatus();
    }
  };

  return (
    <RepresentativeContext.Provider value={{
      isRepresentative,
      repProfileId,
      loading,
      explanations,
      getExplanation,
      updateExplanation,
      refreshStatus
    }}>
      {children}
    </RepresentativeContext.Provider>
  );
}

export function useRepresentative() {
  const context = useContext(RepresentativeContext);
  if (!context) {
    throw new Error('useRepresentative must be used within a RepresentativeProvider');
  }
  return context;
}
